import Button from 'assets/components/button/Button';
import ThemeButtonCircle from 'assets/components/button/themes/Theme.Button.Circle';
import DataMap from 'assets/components/dataMap/DataMap';
import NumberInput from 'assets/components/inputs/number/NumberInput';
import InputWrapper from 'assets/components/inputs/wrappers/InputWrapper';
import ThemeTableSimple2 from 'assets/components/table/themes/Theme.Table.Simple.2';
import ToString from 'assets/components/textBox/ToString';
import { getLocales } from 'assets/locales/Locale';
import Theme from 'assets/themes/Theme.Common';
import PricingDistributionTable from 'module/productManagement/components/pricingDistributionTable/PricingDistributionTable';
import { Fragment, useState } from 'react';
import { useCustomPricingProvider } from '../CustomPricing.Hooks';
import SubfeeDistributionTable from 'module/productManagement/components/pricingDistributionTable/SubfeeDistributionTable';
import TextInput from 'assets/components/inputs/text/TextInput';
import Icon from 'assets/components/icon/Icon';
import FormatValue from 'assets/components/formatValue/FormatValue';
import SelectInput from 'assets/components/inputs/select/SelectInput';
import DistributorDistributionTable from 'module/productManagement/components/pricingDistributionTable/DistributorDistributionTable';
import { compact, reduce } from 'lodash';
import DropdownButton from 'assets/templates/dropdownButton/DropdownButton';
import ThemeDropdownButtonPopup from 'assets/templates/dropdownButton/themes/Theme.DropdownButton.Popup';
import ThemeButtonMenuItem from 'assets/components/button/themes/Theme.Button.MenuItem';
import DealerPricingLimitationTable from 'module/productManagement/components/pricingDistributionTable/DealerPricingLimitationTable';
import DistributionPriceSwitch from 'module/productManagement/components/pricingDistributionTable/DistributionPriceSwitch';
import DealerDistributionDiscountTable from 'module/productManagement/components/pricingDistributionTable/DealerDistributionDiscountTable';
import ThemeOtherVerticalDivider from 'assets/themes/other/Theme.Other.VerticalDivider';
import { addProgressToWindow } from 'assets/components/progressWindow/ProgressWindow';
import { combineStrings } from 'assets/utils/data/String';
import ThemeButtonLink from 'assets/components/button/themes/Theme.Button.Link';

export default function CustomPricingTable() {
  const { lang } = getLocales();
  const {
    getPrice,
    customInfoList,
    productCustomPricePointList,
    terms,
    permissions,
    setEditableId,
    addEmptyPrices,
    updatePrice,
    updateSubfee,
    data,
    removeTerm,
    removeCustomInfo,
    removeCustomInfoGroup,
    productApi,
    isAllowedToCopy,
    copy,
    distributorPricingDistributions,
    dealerPricingSettings,
  } = useCustomPricingProvider();
  const [tempTerm, setTempTerm] = useState<number>();
  const [tempCustomInfo, setTempCustomInfo] = useState<string>();
  const disableInputs = !data.length ? !productCustomPricePointList?.length || !tempCustomInfo || !tempTerm : false;

  const _addEmptyPrices = () => {
    addEmptyPrices(
      tempTerm ? [tempTerm] : terms,
      tempCustomInfo ? [tempCustomInfo] : customInfoList,
      productCustomPricePointList.map((it) => it.id)
    );
    setTempTerm(null);
    setTempCustomInfo(null);
  };
  function updateOriginalPrice({
    termInMonths,
    customInfo,
    productCustomPricePointId,
    distributionKey,
    distributedValue,
  }: {
    termInMonths: number;
    customInfo: string;
    productCustomPricePointId: number;
    distributionKey: string;
    distributedValue: Utils.PricingInfo;
  }) {
    const originalDistributions = getPrice(termInMonths, customInfo, productCustomPricePointId)?.pricingDistributions;
    if (originalDistributions) {
      updatePrice(termInMonths, customInfo, productCustomPricePointId, {
        ...originalDistributions,
        [distributionKey]: distributedValue,
      });
    }
  }
  function updateDistributorPrice({
    termInMonths,
    customInfo,
    productCustomPricePointId,
    distributionKey,
    distributedValue,
  }: {
    termInMonths: number;
    customInfo: string;
    productCustomPricePointId: number;
    distributionKey: string;
    distributedValue: Utils.PricingInfo;
  }) {
    const currentPricePoint = getPrice(termInMonths, customInfo, productCustomPricePointId);
    if (currentPricePoint?.pricingDistributions) {
      const currentDistribution = distributorPricingDistributions.getDistributorPrice(
        currentPricePoint?.id,
        'ProductCustomPricePoint'
      )?.distributions;
      distributorPricingDistributions.updateDistributorPrice({
        pricePointId: currentPricePoint.id,
        pricePointType: 'ProductCustomPricePoint',
        distributions: {
          ...currentDistribution,
          [distributionKey]: distributedValue,
        },
      });
    }
  }

  return (
    <>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          marginBottom: Theme.Size.M,
          gap: Theme.Size.M,
          justifyContent: 'flex-end',
        }}
      >
        <Button
          class={ThemeButtonCircle}
          media="fas-plus"
          render={lang.group}
          data={0}
          onClick={setEditableId}
          style={{ marginRight: 'auto' }}
          htmlElementProps={{ title: `${lang.add} ${lang.pricingPlan}` }}
        />
        {isAllowedToCopy && (
          <Button
            class={ThemeButtonCircle}
            media="fas-copy"
            onClick={copy}
            render={lang.copyDefaultPricesHere}
            style={{ marginRight: 'auto' }}
            htmlElementProps={{ title: lang.copy }}
          />
        )}
        {distributorPricingDistributions.canView && (
          <InputWrapper>
            {!!distributorPricingDistributions.distributors?.length &&
              !distributorPricingDistributions.isDealerOrDistributor && (
                <SelectInput
                  name="distributorId"
                  value={distributorPricingDistributions.distributorId}
                  label={lang.distributors}
                  data={distributorPricingDistributions.distributors?.map((item) => ({
                    ...item.displayInfo,
                    disabled:
                      !!dealerPricingSettings.dealerId && item.id !== dealerPricingSettings.dealer?.representativeId,
                  }))}
                  onChange={({ distributorId }) => distributorPricingDistributions.setDistributorId(distributorId)}
                  style={{ width: '200px' }}
                />
              )}
            <SelectInput
              name="templateId"
              value={distributorPricingDistributions.distributorData?.templateId}
              label={lang.pricingDistributionTemplate}
              data={distributorPricingDistributions.templates?.map((item) => item.displayInfo)}
              onChange={({ templateId }) =>
                distributorPricingDistributions.setDistributorData({
                  templateId,
                  isUpdated: true,
                  distributions: [],
                  template: distributorPricingDistributions.templates?.find((it) => it.id === templateId),
                })
              }
              disabled={!!distributorPricingDistributions.productRelation?.pricingDistributionTemplateId}
              style={{ width: '200px' }}
            />
          </InputWrapper>
        )}
        {dealerPricingSettings.canView && (
          <InputWrapper>
            {!!dealerPricingSettings.dealers?.length && (
              <SelectInput
                name="dealerId"
                value={dealerPricingSettings.dealerId}
                label={lang.dealers}
                data={dealerPricingSettings.dealers?.map((item) => ({
                  ...item.displayInfo,
                  actionIcon: <Icon class="fas-copy" />,
                  actionTitle: 'Copy adjustments to this dealer',
                  disableEdit:
                    !dealerPricingSettings.dealerId ||
                    dealerPricingSettings.dealerId === item.id ||
                    !distributorPricingDistributions.distributorId ||
                    item.representativeId !== distributorPricingDistributions.distributorId,
                  disabled:
                    !!distributorPricingDistributions.distributorId &&
                    item.representativeId !== distributorPricingDistributions.distributorId,
                }))}
                onChange={({ dealerId }) => {
                  dealerPricingSettings.setDealerId(dealerId);
                  const distributorId = dealerPricingSettings.dealers?.find(
                    (it) => it.id === dealerId
                  )?.representativeId;
                  if (distributorId) distributorPricingDistributions.setDistributorId(distributorId);
                }}
                style={{ width: '200px' }}
                onEdit={(id) => dealerPricingSettings.copyTo(id, addProgressToWindow)}
              />
            )}
          </InputWrapper>
        )}
        {distributorPricingDistributions.canEdit && !distributorPricingDistributions.isDealerOrDistributor && (
          <InputWrapper>
            <NumberInput
              label={`${lang.add}: ${lang.term}`}
              name="term"
              value={tempTerm}
              onChange={({ term }) => setTempTerm(term)}
              style={{ formInput: { maxWidth: '200px' } }}
              step={1}
              min={0}
              roundOnBlur
            />
            <TextInput
              label={lang.customInfo}
              name="customInfo"
              value={tempCustomInfo}
              onChange={({ customInfo }) => setTempCustomInfo(customInfo)}
              style={{ formInput: { maxWidth: '200px' } }}
            />
            <Button
              class={ThemeButtonCircle}
              media="fas-plus"
              disabled={disableInputs}
              onClick={_addEmptyPrices}
              style={{ border: `${Theme.Size.XXS} solid ${Theme.Color.shadowDark}` }}
            />
          </InputWrapper>
        )}
      </div>
      <table className={ThemeTableSimple2}>
        <DataMap
          data={productCustomPricePointList}
          render={({ data: productCustomPricePoint }) => (
            <Fragment key={productCustomPricePoint.id}>
              <thead>
                <tr>
                  <th style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                      {!distributorPricingDistributions.isDealerOrDistributor && (
                        <Button
                          media="fas-edit"
                          class={ThemeButtonLink}
                          style={{
                            button: { display: 'inline-block', marginLeft: 'auto', fontSize: Theme.FontSize.M },
                          }}
                          render={lang.edit}
                          data={productCustomPricePoint.id}
                          onClick={setEditableId}
                          htmlElementProps={{
                            title: lang.edit,
                          }}
                        />
                      )}
                      {!distributorPricingDistributions.isDealerOrDistributor && (
                        <Button
                          class={ThemeButtonCircle}
                          style={{ button: { display: 'inline-block' } }}
                          media="fas-trash"
                          onClick={removeCustomInfoGroup}
                          data={productCustomPricePoint.id}
                          disabled={data.some((d) => d.productCustomPricePointId == productCustomPricePoint.id && d.id)}
                        />
                      )}
                      {productCustomPricePoint.customInfoGroup}
                    </div>
                  </th>
                  <DataMap
                    data={terms}
                    render={({ data: termInMonths }) => (
                      <th key={termInMonths} style={{ textAlign: 'right', position: 'relative' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          <div>
                            {dealerPricingSettings.canView && !!dealerPricingSettings.dealer && (
                              <>
                                <DealerPricingLimitationTable
                                  isEditDisabled={dealerPricingSettings.canEdit}
                                  columnKey={`DealerLimitations-${termInMonths.toString()}-${
                                    productCustomPricePoint.id
                                  }`}
                                  rows={customInfoList}
                                  getDiscountPrices={(customInfo) => {
                                    const pricePoint = getPrice(termInMonths, customInfo, productCustomPricePoint.id);
                                    return {
                                      base: dealerPricingSettings.getDiscountPrice(
                                        pricePoint?.id,
                                        'ProductCustomPricePoint',
                                        pricePoint?.pricingDistributions
                                      ),
                                      distributor: dealerPricingSettings.getDistributorDiscountPrice(
                                        pricePoint?.id,
                                        'ProductCustomPricePoint',
                                        distributorPricingDistributions.getDistributorPrice(
                                          pricePoint?.id,
                                          'ProductCustomPricePoint'
                                        )?.distributions
                                      ),
                                    };
                                  }}
                                  getLimitation={(customInfo) =>
                                    dealerPricingSettings.getDistributionLimitation(
                                      getPrice(termInMonths, customInfo, productCustomPricePoint.id)?.id,
                                      'ProductCustomPricePoint'
                                    )?.limitations
                                  }
                                  updateLimitation={(customInfo, limitations) =>
                                    dealerPricingSettings.updateDistributionLimitation({
                                      limitations,
                                      pricePointId: getPrice(termInMonths, customInfo, productCustomPricePoint.id)?.id,
                                      pricePointType: 'ProductCustomPricePoint',
                                    })
                                  }
                                  priceOption={({ delta, property, unit }) => (
                                    <DropdownButton
                                      buttonMedia="fas-copy"
                                      horizontalPosition="auto_fixed"
                                      verticalPosition="auto_fixed"
                                      tabIndex={null}
                                      subcomponents={ThemeDropdownButtonPopup}
                                      htmlElementProps={{ title: lang.copy }}
                                      title={lang.increaseAll}
                                      disabled={unit !== 'replacement' || delta < 0}
                                      dropdownOptions={({ animateOut }) => (
                                        <Button
                                          htmlElementProps={{ title: '' }}
                                          class={ThemeButtonMenuItem}
                                          onClick={() => {
                                            animateOut();
                                            dealerPricingSettings.updateDistributionLimitation(
                                              compact(
                                                data.map((pricePoint) => {
                                                  const currentLimitations =
                                                    dealerPricingSettings.getDistributionLimitation(
                                                      pricePoint?.id,
                                                      'ProductCustomPricePoint'
                                                    );
                                                  if (
                                                    !currentLimitations?.limitations?.unit ||
                                                    currentLimitations?.limitations.unit === unit
                                                  ) {
                                                    const currentBasePrice =
                                                      dealerPricingSettings.getDiscountPrice(
                                                        pricePoint?.id,
                                                        'ProductCustomPricePoint',
                                                        pricePoint.pricingDistributions
                                                      ) +
                                                      reduce(
                                                        distributorPricingDistributions.getDistributorPrice(
                                                          pricePoint?.id,
                                                          'ProductCustomPricePoint'
                                                        )?.distributions,
                                                        (prev, curr) => prev + curr.amount,
                                                        0
                                                      );
                                                    return {
                                                      ...currentLimitations,
                                                      limitations: {
                                                        ...currentLimitations?.limitations,
                                                        unit,
                                                        [property]: delta === 0 ? null : currentBasePrice + delta,
                                                      },
                                                      pricePointId: pricePoint?.id,
                                                      pricePointType: 'ProductCustomPricePoint',
                                                    };
                                                  }
                                                  return null;
                                                })
                                              )
                                            );
                                          }}
                                        >
                                          <span>
                                            {property === 'minAmount' ? lang.sugRetail : lang.max} {lang.prices}{' '}
                                            {lang.by} <FormatValue value={`${delta.toFixed(2)}$`} />
                                          </span>
                                        </Button>
                                      )}
                                    />
                                  )}
                                />
                                <DealerDistributionDiscountTable
                                  isEditDisabled={dealerPricingSettings.canEdit}
                                  columnKey={`DealerDistributionDiscount-${termInMonths.toString()}-${
                                    productCustomPricePoint.id
                                  }`}
                                  rows={customInfoList}
                                  columns={productApi?.payload?.templateKeys}
                                  getDiscount={(customInfo) =>
                                    dealerPricingSettings.getDistributionDiscount(
                                      getPrice(termInMonths, customInfo, productCustomPricePoint.id)?.id,
                                      'ProductCustomPricePoint'
                                    )?.distributionDiscount
                                  }
                                  updateDiscount={(customInfo, distribution) => {
                                    if (productApi.payload.id && dealerPricingSettings.dealerId) {
                                      const pricePoint = getPrice(termInMonths, customInfo, productCustomPricePoint.id);
                                      const dealerSettings = dealerPricingSettings.getDistributionDiscount(
                                        pricePoint?.id,
                                        'ProductCustomPricePoint'
                                      );
                                      dealerPricingSettings.updateDistributionDiscount({
                                        ...dealerSettings,
                                        productId: productApi.payload.id,
                                        dealerId: dealerPricingSettings.dealerId,
                                        pricePointId: pricePoint?.id,
                                        pricePointType: 'ProductCustomPricePoint',
                                        distributionDiscount: {
                                          ...dealerSettings?.distributionDiscount,
                                          ...distribution,
                                        },
                                      });
                                    }
                                  }}
                                />
                                <div className={ThemeOtherVerticalDivider} />
                              </>
                            )}
                            {distributorPricingDistributions.canView &&
                              !!distributorPricingDistributions.distributor && (
                                <>
                                  <DistributorDistributionTable
                                    isEditDisabled={distributorPricingDistributions.canEdit}
                                    columnKey={`Distributor-${termInMonths.toString()}-${productCustomPricePoint.id}`}
                                    rows={customInfoList}
                                    columns={distributorPricingDistributions.distributorData?.template?.keys}
                                    getPrice={(customInfo) =>
                                      distributorPricingDistributions.getDistributorPrice(
                                        getPrice(termInMonths, customInfo, productCustomPricePoint.id)?.id,
                                        'ProductCustomPricePoint'
                                      )?.distributions
                                    }
                                    updatePrice={(customInfo, distributions) =>
                                      distributorPricingDistributions.updateDistributorPrice({
                                        distributions,
                                        pricePointId: getPrice(termInMonths, customInfo, productCustomPricePoint.id)
                                          ?.id,
                                        pricePointType: 'ProductCustomPricePoint',
                                      })
                                    }
                                    priceOption={({ distributedValue, distributionKey }) => (
                                      <DropdownButton
                                        buttonMedia="fas-copy"
                                        horizontalPosition="auto_fixed"
                                        verticalPosition="auto_fixed"
                                        tabIndex={null}
                                        subcomponents={ThemeDropdownButtonPopup}
                                        htmlElementProps={{ title: lang.copy }}
                                        title={lang.copyTo}
                                        dropdownOptions={({ animateOut }) => (
                                          <>
                                            <Button
                                              class={ThemeButtonMenuItem}
                                              onClick={() => {
                                                animateOut();
                                                for (const customInfo of customInfoList) {
                                                  updateDistributorPrice({
                                                    termInMonths,
                                                    customInfo,
                                                    productCustomPricePointId: productCustomPricePoint.id,
                                                    distributionKey,
                                                    distributedValue,
                                                  });
                                                }
                                              }}
                                              htmlElementProps={{ title: '' }}
                                            >
                                              {lang.thisColumn}
                                            </Button>
                                            <Button
                                              class={ThemeButtonMenuItem}
                                              onClick={() => {
                                                animateOut();
                                                for (const customInfo of customInfoList) {
                                                  for (const productCustomPricePoint of productCustomPricePointList) {
                                                    updateDistributorPrice({
                                                      termInMonths,
                                                      customInfo,
                                                      productCustomPricePointId: productCustomPricePoint.id,
                                                      distributionKey,
                                                      distributedValue,
                                                    });
                                                  }
                                                }
                                              }}
                                              htmlElementProps={{ title: '' }}
                                            >
                                              {lang.allGroups}
                                            </Button>
                                          </>
                                        )}
                                      />
                                    )}
                                  />
                                  {dealerPricingSettings.canView && !!dealerPricingSettings.dealer && (
                                    <DealerDistributionDiscountTable
                                      isEditDisabled={dealerPricingSettings.canEdit}
                                      isDistributorDiscount
                                      columnKey={`DealerDistributorDistributionDiscount-${termInMonths.toString()}-${
                                        productCustomPricePoint.id
                                      }`}
                                      rows={customInfoList}
                                      columns={distributorPricingDistributions.distributorData?.template?.keys}
                                      getDiscount={(customInfo) =>
                                        dealerPricingSettings.getDistributionDiscount(
                                          getPrice(termInMonths, customInfo, productCustomPricePoint.id)?.id,
                                          'ProductCustomPricePoint'
                                        )?.distributorDistributionDiscount
                                      }
                                      updateDiscount={(customInfo, distribution) => {
                                        const pricePoint = getPrice(
                                          termInMonths,
                                          customInfo,
                                          productCustomPricePoint.id
                                        );
                                        if (productApi.payload.id && dealerPricingSettings.dealerId) {
                                          const dealerSettings = dealerPricingSettings.getDistributionDiscount(
                                            pricePoint?.id,
                                            'ProductCustomPricePoint'
                                          );
                                          dealerPricingSettings.updateDistributionDiscount({
                                            ...dealerSettings,
                                            productId: productApi.payload.id,
                                            dealerId: dealerPricingSettings.dealerId,
                                            pricePointId: pricePoint?.id,
                                            pricePointType: 'ProductCustomPricePoint',
                                            distributorDistributionDiscount: {
                                              ...dealerSettings?.distributorDistributionDiscount,
                                              ...distribution,
                                            },
                                          });
                                        }
                                      }}
                                    />
                                  )}
                                  <div className={ThemeOtherVerticalDivider} />
                                </>
                              )}

                            {!distributorPricingDistributions.isDealerOrDistributor && (
                              <>
                                <PricingDistributionTable
                                  isEditDisabled={distributorPricingDistributions.canEdit}
                                  columnKey={`PricingDistribution-${termInMonths.toString()}-${
                                    productCustomPricePoint.id
                                  }`}
                                  rows={customInfoList}
                                  columns={productApi?.payload?.templateKeys}
                                  getPrice={(customInfo) =>
                                    getPrice(termInMonths, customInfo, productCustomPricePoint.id)?.pricingDistributions
                                  }
                                  updatePrice={(customInfo, distribution) =>
                                    updatePrice(termInMonths, customInfo, productCustomPricePoint.id, distribution)
                                  }
                                  priceOption={({ distributedValue, distributionKey }) => (
                                    <DropdownButton
                                      buttonMedia="fas-copy"
                                      horizontalPosition="auto_fixed"
                                      verticalPosition="auto_fixed"
                                      tabIndex={null}
                                      subcomponents={ThemeDropdownButtonPopup}
                                      htmlElementProps={{ title: lang.copy }}
                                      title={lang.copyTo}
                                      dropdownOptions={({ animateOut }) => (
                                        <>
                                          <Button
                                            class={ThemeButtonMenuItem}
                                            onClick={() => {
                                              animateOut();
                                              for (const customInfo of customInfoList) {
                                                updateOriginalPrice({
                                                  termInMonths,
                                                  customInfo,
                                                  productCustomPricePointId: productCustomPricePoint.id,
                                                  distributionKey,
                                                  distributedValue,
                                                });
                                              }
                                            }}
                                            htmlElementProps={{ title: '' }}
                                          >
                                            {lang.thisColumn}
                                          </Button>
                                          <Button
                                            class={ThemeButtonMenuItem}
                                            onClick={() => {
                                              animateOut();
                                              for (const customInfo of customInfoList) {
                                                for (const productCustomPricePoint of productCustomPricePointList) {
                                                  updateOriginalPrice({
                                                    termInMonths,
                                                    customInfo,
                                                    productCustomPricePointId: productCustomPricePoint.id,
                                                    distributionKey,
                                                    distributedValue,
                                                  });
                                                }
                                              }
                                            }}
                                            htmlElementProps={{ title: '' }}
                                          >
                                            {lang.allGroups}
                                          </Button>
                                        </>
                                      )}
                                    />
                                  )}
                                />
                                <SubfeeDistributionTable
                                  isEditDisabled={distributorPricingDistributions.canEdit}
                                  columnKey={`Subfees-${termInMonths.toString()}-${productCustomPricePoint.id}`}
                                  rows={customInfoList}
                                  columns={productApi?.payload?.subfee}
                                  getPrice={(customInfo) =>
                                    getPrice(termInMonths, customInfo, productCustomPricePoint.id)?.subfees
                                  }
                                  updatePrice={(customInfo, subfees) =>
                                    updateSubfee(termInMonths, customInfo, productCustomPricePoint.id, subfees)
                                  }
                                />
                                <Button
                                  class={ThemeButtonCircle}
                                  style={{ button: { display: 'inline-block' } }}
                                  media="fas-trash"
                                  onClick={removeTerm}
                                  data={termInMonths}
                                  disabled={
                                    data.some((d) => d.termInMonths === termInMonths && d.id) ||
                                    permissions.DELETE_CUSTOMPRICEPOINT
                                  }
                                  htmlElementProps={{ title: `${lang.clear} ${lang.term}` }}
                                />
                              </>
                            )}
                          </div>
                          <ToString value={`${termInMonths}m`} />
                        </div>
                      </th>
                    )}
                  />
                </tr>
              </thead>
              <tbody>
                <DataMap
                  data={customInfoList}
                  render={({ data: customInfo }) => (
                    <tr key={customInfo}>
                      <td style={{ textAlign: 'right', whiteSpace: 'nowrap' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                          {!distributorPricingDistributions.isDealerOrDistributor && (
                            <Button
                              class={ThemeButtonCircle}
                              style={{ button: { display: 'inline-block' } }}
                              media="fas-trash"
                              onClick={removeCustomInfo}
                              data={customInfo}
                              disabled={
                                data.some((d) => d.customInfo === customInfo && d.id) ||
                                permissions.DELETE_CUSTOMPRICEPOINT
                              }
                              htmlElementProps={{ title: lang.clearDistributorPrice }}
                            />
                          )}
                          {customInfo}
                        </div>
                      </td>
                      <DataMap
                        data={terms}
                        render={({ data: termInMonths }) => {
                          const pricePoint = getPrice(termInMonths, customInfo, productCustomPricePoint.id);
                          const isUnset = pricePoint?.priceCAD == null;

                          const distributorDistribution = distributorPricingDistributions.getDistributorPrice(
                            pricePoint?.id,
                            'ProductCustomPricePoint'
                          );
                          const distributorPriceSegment = reduce(
                            distributorDistribution?.distributions || {},
                            (prev, curr) => prev + curr.amount,
                            0
                          );
                          const basePrice = pricePoint?.priceCAD ?? 0;
                          const distributorPrice = basePrice + distributorPriceSegment;
                          const basePriceWithDiscount = dealerPricingSettings.getDiscountPrice(
                            pricePoint?.id,
                            'ProductCustomPricePoint',
                            pricePoint?.pricingDistributions
                          );
                          const distributorPriceWithDiscount = dealerPricingSettings.getDistributorDiscountPrice(
                            pricePoint?.id,
                            'ProductCustomPricePoint',
                            distributorDistribution?.distributions
                          );
                          const finalPrice = basePriceWithDiscount + distributorPriceWithDiscount;

                          const showDealerPrice = distributorPrice > 0 && !!dealerPricingSettings.dealerId;
                          const showDistributorPrice =
                            distributorPrice > 0 && !!distributorPricingDistributions.distributorId;
                          return (
                            <td style={{ textAlign: 'right' }} key={termInMonths}>
                              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <div style={{ display: 'flex' }}>
                                  {distributorPricingDistributions.canView &&
                                    !!distributorPricingDistributions.distributorData?.template?.keys && (
                                      <Button
                                        disabled={!distributorDistribution || permissions.DELETE_CUSTOMPRICEPOINT}
                                        onClick={() =>
                                          distributorPricingDistributions.updateDistributorPrice({
                                            pricePointId: pricePoint?.id,
                                            distributions: {},
                                            pricePointType: 'ProductCustomPricePoint',
                                          })
                                        }
                                        class={{ button: ThemeButtonCircle }}
                                        media={<Icon class="fas-user-slash" />}
                                        htmlElementProps={{ title: lang.clearDistributorPrice }}
                                      />
                                    )}
                                  {!distributorPricingDistributions.isDealerOrDistributor && (
                                    <Button
                                      disabled={isUnset || permissions.DELETE_CUSTOMPRICEPOINT}
                                      onClick={() => updatePrice(termInMonths, customInfo, productCustomPricePoint.id)}
                                      class={{ button: ThemeButtonCircle }}
                                      media={<Icon class="fas-trash-alt" />}
                                      htmlElementProps={{ title: `${lang.clear} ${lang.price}` }}
                                    />
                                  )}
                                </div>
                                {showDealerPrice || showDistributorPrice ? (
                                  <span
                                    style={{
                                      fontWeight: isUnset ? undefined : 'bold',
                                      color: isUnset ? Theme.Color.warning : undefined,
                                    }}
                                  >
                                    <span style={{ fontSize: Theme.FontSize.S }}>
                                      {combineStrings(
                                        '/',
                                        showDealerPrice ? lang.dealer : undefined,
                                        showDistributorPrice ? lang.distributor : undefined,
                                        lang.base
                                      )}
                                    </span>
                                    <br />
                                    <span style={{ fontSize: Theme.FontSize.S }}>
                                      {showDealerPrice && (
                                        <>
                                          <FormatValue value={isUnset ? 'N/A' : `${finalPrice.toFixed(2)}$`} />/
                                        </>
                                      )}
                                      {showDistributorPrice && (
                                        <>
                                          <FormatValue value={isUnset ? 'N/A' : `${distributorPrice.toFixed(2)}$`} />/
                                        </>
                                      )}
                                      <FormatValue value={isUnset ? 'N/A' : `${basePrice.toFixed(2)}$`} />
                                    </span>
                                  </span>
                                ) : (
                                  <span
                                    style={{
                                      fontWeight: isUnset ? undefined : 'bold',
                                      color: isUnset ? Theme.Color.warning : undefined,
                                    }}
                                  >
                                    <FormatValue value={isUnset ? 'N/A' : `${basePrice.toFixed(2)}$`} />
                                  </span>
                                )}
                              </div>
                            </td>
                          );
                        }}
                      />
                    </tr>
                  )}
                />
              </tbody>
            </Fragment>
          )}
        />
      </table>
      <DistributionPriceSwitch
        templateKeys={productApi?.payload?.templateKeys ?? []}
        onUpdate={(firstKey, secondKey) => {
          for (const termInMonths of terms) {
            for (const customInfo of customInfoList) {
              for (const productCustomPricePoint of productCustomPricePointList) {
                const originalDistributions = getPrice(
                  termInMonths,
                  customInfo,
                  productCustomPricePoint.id
                )?.pricingDistributions;
                if (originalDistributions) {
                  updatePrice(termInMonths, customInfo, productCustomPricePoint.id, {
                    ...originalDistributions,
                    [firstKey]: originalDistributions[secondKey],
                    [secondKey]: originalDistributions[firstKey],
                  });
                }
              }
            }
          }
        }}
      />
    </>
  );
}
